'use client';

import { useQuery } from '@tanstack/react-query';
import OilyticsClient from 'api/OilyticsClient';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from 'components/ui/sidebar';
import { Role } from 'consts/auth';
import { UPGRADE_ACCESS_MODAL_ID } from 'consts/modals';
import { requiresFullAccess } from 'consts/route-access';
import { useModals } from 'contexts/modals';
import { useHasRole, useIsPLOnly } from 'hooks/use-auth';
import { useCurrentUser } from 'hooks/use-current-user';
import { useDashboardsList } from 'hooks/use-dashboards';
import every from 'lodash/every';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import flatMap from 'lodash/flatMap';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import map from 'lodash/map';
import maxBy from 'lodash/maxBy';
import reject from 'lodash/reject';
import replace from 'lodash/replace';
import some from 'lodash/some';
import sortBy from 'lodash/sortBy';
import split from 'lodash/split';
import startsWith from 'lodash/startsWith';
import toLower from 'lodash/toLower';
import {
  Anchor,
  BarChart2,
  ChevronRight,
  Crosshair,
  Database,
  FileChartColumn,
  FileText,
  Globe,
  LayoutDashboard,
  Lock,
  NotebookText,
  Ship,
} from 'lucide-react';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { SidebarFloatingMenu } from './sidebar-floating-menu';

// Define the items for the sidebar
type SidebarItem = {
  title: string;
  url: string;
  icon?: React.ElementType;
  isActive?: boolean;
  // Other pages the item stands for, highlighted as this item (e.g. Margins on /unit-margins).
  alsoActiveOn?: string[];
  items?: {
    title: string;
    url: string;
    locked?: boolean;
  }[];
};

const sidebarItems: SidebarItem[] = [
  {
    title: 'Watchlists',
    url: '/watchlists',
    icon: FileChartColumn,
    items: [],
  },
  {
    title: 'Margins',
    // Opens Refinery Margins; Unit Margins is a tab on the page.
    url: '/refinery-margins',
    alsoActiveOn: ['/unit-margins'],
    icon: BarChart2,
    items: [],
  },
  {
    title: 'Fundamentals Summary',
    url: '/fundamentals-summary',
    icon: FileText,
    items: [
      { title: 'Balances', url: '/dashboard/397' },
      { title: 'Demand', url: '/dashboard/413' },
      { title: 'Output & Yield', url: '/dashboard/410' },
      { title: 'Refinery Runs', url: '/dashboard/408' },
      { title: 'Stocks', url: '/dashboard/409' },
      { title: 'Supply', url: '/dashboard/407' },
    ],
  },
  {
    title: 'Commitment of Traders',
    url: '/cot',
    icon: Database,
    items: [],
  },
  {
    title: 'Open Interest',
    url: '/open-interest',
    icon: BarChart2,
    items: [],
  },
  {
    title: 'Global Flows',
    url: '/global-flows',
    icon: Globe,
    items: [],
  },
  {
    title: 'Hormuz Flows',
    url: '/hormuz-flows',
    icon: Anchor,
    items: [],
  },
  {
    title: 'Geopolitics',
    url: '/geopolitics',
    icon: Crosshair,
    items: [],
  },
  {
    title: 'Dashboards',
    url: '/dashboard',
    icon: LayoutDashboard,
    items: [],
  },
  {
    title: 'Daily Reports',
    url: '/daily-reports',
    icon: NotebookText,
    items: [],
  },
];

// Add a type guard for plDashboards
function isDashboardArray (arr: any): arr is { title: string; url: string }[] {
  return isArray(arr) && every(arr, item => isObject(item) && item !== null && 'title' in item && 'url' in item);
}

// Strip the query string so URLs like '/dashboard/398?pageName=...' compare cleanly against location.pathname.
const pathFromUrl = (url: string) => split(url, '?')[0];

// True when `pathname` is `target` exactly or a child of it (e.g. /watchlists/123).
const matchesPath = (pathname: string, target: string) =>
  pathname === target || startsWith(pathname, target + '/');

// Insert `newItem` immediately after the item whose `title` matches `targetTitle`.
// Falls back to appending if no match. Used so the Petro-Logistics entry slots in
// after Global Flows when present, without three near-duplicate branches.
function insertAfter<T extends { title: string }> (items: T[], targetTitle: string, newItem: T): T[] {
  const idx = findIndex(items, ['title', targetTitle]);
  if (idx === -1) return [...items, newItem];
  return [...items.slice(0, idx + 1), newItem, ...items.slice(idx + 1)];
}

// Renders the apps nav as a fragment of <SidebarMenuItem>s. Consumers own the
// surrounding <SidebarMenu> so collapsed mode can compose apps + agents into
// a single list.
export function NavApps ({
  items: navItems = sidebarItems,
}: {
  items?: SidebarItem[]
}) {

  const { state: sidebarState, isMobile } = useSidebar();
  const { data: dashboardsList } = useDashboardsList(true);
  // Free / expired-trial users see FullAccess entries locked rather than
  // hidden — the point is to advertise what a subscription unlocks. Which
  // URLs those are comes from consts/route-access, the same list PrivateRoute
  // gates on, so a locked entry always matches the route behind it. Note
  // useHasRole treats a MISSING roles claim as FullAccess (mirroring the
  // backend's has_full_access), so pre-freemium tokens never flash locks.
  const hasFullAccess = useHasRole(Role.FullAccess);
  const { openModal } = useModals();
  // HACK: PL-only users see only the Petro-Logistics Shiptracking nav item.
  // Frontend-only gate; backend APIs are still reachable. Replace with proper
  // server-side enforcement.
  const isPLOnly = useIsPLOnly();
  // Organization-level gates come from the server with the user (User.can_access_*), so the sidebar and the
  // API agree; a missing flag (older server) shows the entry.
  const { data: currentUser } = useCurrentUser();
  const canAccessHormuzFlows = currentUser?.settings?.can_access_hormuz_flows !== false;
  type Dashboard = { name: string; url?: string; id: string | number };
  const plDashboards = sortBy(
    map(
      filter(dashboardsList || [], (dashboard: Dashboard) =>
        includes(toLower(dashboard.name), toLower('Petro-Logistics')) &&
        replace(dashboard.name, 'Petro-Logistics ', '') !== 'Summary Page'
      ),
      (dashboard: Dashboard) => ({
        title: replace(dashboard.name, 'Petro-Logistics ', ''),
        url: dashboard.url || `/dashboard/${dashboard.id}`,
      })
    ),
    'title'
  );

  // Fetch public watchlists. The backend already lists 'paid' templates
  // alongside 'free' ones for every caller (see get_public_watchlists) — it
  // just withholds their trades/groups until the caller has FullAccess — so
  // the nav can show the paid names locked instead of hiding them outright.
  const { data: publicWatchlists = [] } = useQuery({
    queryKey: ['public-watchlists'],
    queryFn: async () => {
      const client = await OilyticsClient.create();
      const res = await client.getPublicWatchlists();
      return res.data as { id: string; name: string; visibility?: 'private' | 'paid' | 'free' }[];
    },
  });

  // Process public watchlists: sort alphabetically, remove "Oilytics " prefix,
  // and lock 'paid' entries for users without FullAccess.
  const publicWatchlistItems = React.useMemo(() => {
    return sortBy(
      map(publicWatchlists, (watchlist) => {
        let title = watchlist.name;
        if (startsWith(title, 'Oilytics ')) {
          title = replace(title, 'Oilytics ', '');
        }
        return {
          title,
          url: `/watchlists/${watchlist.id}`,
          locked: !hasFullAccess && watchlist.visibility === 'paid',
        };
      }),
      'title'
    );
  }, [publicWatchlists, hasFullAccess]);

  // Combine items with plDashboards and public watchlists
  const combinedItems = React.useMemo(() => {
    const plSubItems = isDashboardArray(plDashboards) ? plDashboards : [];

    // HACK: PL-only users get a sidebar containing nothing but the
    // Petro-Logistics Shiptracking entry. Everything else (Agents/MCP UI
    // included) is hidden. Strictly a UX gate — backend still serves the
    // data. Note the URL differs from the non-PL entry below.
    if (isPLOnly) {
      return [{
        title: 'Petro-Logistics Shiptracking',
        url: '/petrologistics-shiptracking',
        icon: Ship,
        items: plSubItems,
      }];
    }

    const visibleItems = canAccessHormuzFlows ? navItems : reject(navItems, ['url', '/hormuz-flows']);
    const withWatchlists = map(visibleItems, (item) =>
      item.title === 'Watchlists' ? { ...item, items: publicWatchlistItems } : item
    );

    if (plDashboards.length === 0) return withWatchlists;

    return insertAfter(withWatchlists, 'Global Flows', {
      title: 'Petro-Logistics Shiptracking',
      url: '/dashboard/416',
      icon: Ship,
      items: plSubItems,
    });
  }, [navItems, plDashboards, publicWatchlistItems, isPLOnly, canAccessHormuzFlows]);

  const { pathname } = useLocation();
  // Resolve which item URL "owns" the current pathname using longest-prefix-wins.
  // Example: on /dashboard/398, both Margins (/dashboard/398) and Dashboards (/dashboard)
  // are matches; the longer/more specific path wins.
  const activePath = React.useMemo(() => {
    const allUrls = flatMap(combinedItems as SidebarItem[], (item) => [
      pathFromUrl(item.url),
      ...(item.alsoActiveOn ?? []),
      ...map(
        isArray(item.items) ? item.items as { url: string }[] : [],
        (sub) => pathFromUrl(sub.url),
      ),
    ]);
    const matching = filter(allUrls, (target) => matchesPath(pathname, target));
    return maxBy(matching, (p) => p.length);
  }, [combinedItems, pathname]);

  return (
    <>
      {map(combinedItems as SidebarItem[], (item) => {
        const isLocked = !hasFullAccess && requiresFullAccess(item.url);
        const subItems: { title: string; url: string; locked?: boolean }[] =
            isArray(item.items) &&
            every(item.items, i => isObject(i) && i !== null && 'title' in i && 'url' in i)
              ? (item.items as { title: string; url: string; locked?: boolean }[])
              : [];
        // A locked parent's children are FullAccess too, so there is nothing
        // to expand into — drop the chevron and the collapsed-mode flyout.
        const hasSubItems = subItems.length > 0 && !isLocked;
        const shouldRenderFloatingSubmenu = hasSubItems && sidebarState === 'collapsed' && !isMobile;
        const itemPath = pathFromUrl(item.url);
        // Item is active when it owns the current pathname OR when one of its
        // sub-items does (so the parent stays highlighted on detail pages).
        const isItemActive =
          itemPath === activePath ||
          includes(item.alsoActiveOn, activePath) ||
          some(subItems, (sub) => pathFromUrl(sub.url) === activePath);
        const menuButton = (
          <SidebarMenuButton
            asChild
            // size="sm" → h-7 text-xs to match the denser SVG layout
            size="sm"
            isActive={isItemActive}
            tooltip={shouldRenderFloatingSubmenu ? undefined : item.title}
            className="flex-grow"
          >
            {isLocked ? (
              // Keep the label visible and pitch the upgrade instead of
              // navigating: PrivateRoute would only bounce them to /chat.
              <button type="button" onClick={() => openModal(UPGRADE_ACCESS_MODAL_ID)}>
                {item.icon && <item.icon />}
                {/* The variant's [&>span:last-child]:truncate no longer
                    matches once the lock trails the label, so truncate here. */}
                <span className="truncate">{item.title}</span>
                <Lock className="ml-auto !size-3.5 opacity-60 group-data-[collapsible=icon]:hidden" />
              </button>
            ) : (
              <NavLink to={item.url}>
                {item.icon && <item.icon />}
                <span>{item.title}</span>
              </NavLink>
            )}
          </SidebarMenuButton>
        );

        return (
          <Collapsible key={item.title} defaultOpen={Boolean((item as { isActive?: boolean }).isActive)} className="group/collapsible">
            <SidebarMenuItem className="flex flex-col">
              <div className="flex w-full items-center">
                {shouldRenderFloatingSubmenu
                  ? (
                    <SidebarFloatingMenu
                      trigger={menuButton}
                      title={item.title}
                      items={subItems}
                      onLockedClick={() => openModal(UPGRADE_ACCESS_MODAL_ID)}
                    />
                  )
                  : menuButton}

                {hasSubItems && (
                  <CollapsibleTrigger className="ml-auto p-1 hover:bg-sidebar-accent rounded-md group-data-[collapsible=icon]:hidden">
                    <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </CollapsibleTrigger>
                )}
              </div>

              {hasSubItems && (
                <CollapsibleContent className="animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden transition-all">
                  <SidebarMenuSub>
                    {map(subItems, (subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild size="sm" isActive={pathFromUrl(subItem.url) === activePath}>
                          {subItem.locked ? (
                            // Paid watchlist templates: advertise the name but
                            // pitch the upgrade instead of navigating into
                            // content the caller's tier can't unlock.
                            <button type="button" onClick={() => openModal(UPGRADE_ACCESS_MODAL_ID)} className="text-muted-foreground">
                              <span className="truncate">{subItem.title}</span>
                              <Lock className="ml-auto !size-3 opacity-60" />
                            </button>
                          ) : (
                            <NavLink to={subItem.url}>
                              <span>{subItem.title}</span>
                            </NavLink>
                          )}
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              )}
            </SidebarMenuItem>
          </Collapsible>
        );
      })}
    </>
  );
}
